import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Phone, Delete, RefreshCw, Grid3X3 } from 'lucide-react';

interface OtpPanelProps {
  title: string;
  phone?: string | null;
  code: string;
  onCodeChange: (code: string) => void;
  onSubmit: () => void;
  onResend?: () => Promise<void> | void;
  onEdit?: () => void;
  editLabel?: string;
  error?: string;
  hint?: string | null;
  loading?: boolean;
  /** Qayta yuborishgacha soniya */
  resendSeconds?: number;
  /** Kod amal qilish muddati (ISO) — berilsa, sanoq ko'rsatiladi */
  expiresAt?: string | null;
}

function secondsUntil(iso?: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Number.isNaN(ms) ? null : Math.max(0, Math.ceil(ms / 1000));
}

function formatMmSs(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * OTP paneli: 6 raqamli kod, paste qo'yish, to'lganda avtomatik tasdiqlash,
 * qayta yuborish sanog'i va tablet uchun raqamli klaviatura.
 */
export function OtpPanel({
  title,
  phone,
  code,
  onCodeChange,
  onSubmit,
  onResend,
  onEdit,
  editLabel,
  error,
  hint,
  loading,
  resendSeconds = 60,
  expiresAt,
}: OtpPanelProps) {
  const { t } = useTranslation();
  const [seconds, setSeconds] = useState(resendSeconds);
  const [expiresIn, setExpiresIn] = useState(() => secondsUntil(expiresAt));
  const expired = expiresIn === 0;
  const [numpad, setNumpad] = useState(false);
  const submittedFor = useRef<string | null>(null);

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setInterval(() => setSeconds(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [seconds]);

  useEffect(() => {
    setExpiresIn(secondsUntil(expiresAt));
    if (!expiresAt) return;
    const t = setInterval(() => setExpiresIn(secondsUntil(expiresAt)), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  // Kod to'liq bo'lганda avtomatik tasdiqlash (bir marta)
  useEffect(() => {
    if (code.length === 6 && !loading && !expired && submittedFor.current !== code) {
      submittedFor.current = code;
      onSubmit();
    }
    if (code.length < 6) submittedFor.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, loading, expired]);

  const setDigits = (val: string) => onCodeChange(val.replace(/\D/g, '').slice(0, 6));

  const handleResend = async () => {
    if (!onResend || (seconds > 0 && !expired)) return;
    await onResend();
    onCodeChange('');
    submittedFor.current = null;
    setSeconds(resendSeconds);
  };

  return (
    <div className="space-y-4 rounded-xl border border-border bg-muted/50 p-5 text-center">
      <Phone size={30} className="mx-auto text-primary" />
      <p className="text-lg font-semibold text-foreground">{title}</p>
      {phone && <p className="text-base text-muted-foreground">{t('pos_otp.sent', { phone })}</p>}

      {hint && (
        <p className="inline-block rounded-xl bg-warning/10 px-4 py-2 font-mono text-sm text-warning">
          {t('pos_otp.test_mode', { hint: hint.replace(/^Mock kod:\s*/i, `${t('pos_otp.code_prefix')} `) })}
        </p>
      )}

      <Input
        placeholder="••••••"
        value={code}
        onChange={e => setDigits(e.target.value)}
        onPaste={e => {
          e.preventDefault();
          setDigits(e.clipboardData.getData('text'));
        }}
        onKeyDown={e => { if (e.key === 'Enter' && code.length === 6 && !expired) onSubmit(); }}
        className={`h-16 text-center font-mono text-3xl tracking-[0.4em] ${error ? 'border-destructive' : ''}`}
        maxLength={6}
        autoFocus
        inputMode="numeric"
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
      {expiresIn !== null && (
        expired
          ? <p className="text-sm text-destructive">{t('otp.expired')}</p>
          : <p className="text-sm text-muted-foreground">{t('otp.expires_in', { time: formatMmSs(expiresIn) })}</p>
      )}

      {numpad && (
        <div className="mx-auto grid max-w-xs grid-cols-3 gap-2">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(d => (
            <Button key={d} type="button" variant="outline" className="h-14 text-xl font-semibold"
              onClick={() => setDigits(code + d)}>{d}</Button>
          ))}
          <Button type="button" variant="ghost" className="h-14" onClick={() => onCodeChange('')}>C</Button>
          <Button type="button" variant="outline" className="h-14 text-xl font-semibold"
            onClick={() => setDigits(code + '0')}>0</Button>
          <Button type="button" variant="ghost" className="h-14" onClick={() => onCodeChange(code.slice(0, -1))}>
            <Delete size={20} />
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-center gap-2">
        {onResend && (
          <Button variant="ghost" className="h-12 text-base" onClick={handleResend} disabled={loading || (seconds > 0 && !expired)}>
            <RefreshCw size={16} className="mr-2" />
            {seconds > 0 && !expired ? t('pos_otp.resend_in', { seconds }) : t('pos_otp.resend')}
          </Button>
        )}
        <Button variant="ghost" className="h-12 text-base" onClick={() => setNumpad(v => !v)}>
          <Grid3X3 size={16} className="mr-2" />
          {numpad ? t('pos_otp.hide_numpad') : t('pos_otp.show_numpad')}
        </Button>
        {onEdit && (
          <Button variant="ghost" className="h-12 text-base" onClick={onEdit} disabled={loading}>
            {editLabel ?? t('pos_otp.edit_default')}
          </Button>
        )}
      </div>
    </div>
  );
}
